/**
 * tax/taxUi.tsx — 세무사 사이트 디자인 토큰 · 공용 부품
 *  2026-10-09 지도사가 맡긴 디자인 시안(임지도사협업방 zip · README 디자인 토큰)대로:
 *  바탕 #F3F4F6 · 카드 흰색 모서리 20 · 글자 #17191C / 보조 #6B7280 / 흐림 #8B9099 · 주요색 #2F6BF0 · 고른 칸 #F2F6FF
 *  입력칸 54 · 모서리 12 · 회색 바탕(포커스 = 파란 테두리 + 흰 바탕, TaxApp 전역 CSS) · 칩 42 · 메인 버튼 58 · 단계 표시 「1 / 4」
 */
import React from 'react';

export const X = {
  navy: '#17191C', blue: '#2F6BF0', blueDk: '#1F55CF', blueLt: '#F2F6FF', blueOff: '#C9D8FB', blueTint: '#E8F0FE', gold: '#B4610B', goldLt: '#FFF1E0',
  teal: '#0F766E', bg: '#F3F4F6', card: '#FFFFFF', field: '#F3F4F6', soft: '#F0F2F5', line: '#E6E8EC', lineDk: '#D5D9DF', dot: '#DCDFE4',
  text: '#17191C', label: '#4B5058', sub: '#6B7280', muted: '#8B9099', red: '#D22F2F', redLt: '#FDECEC', green: '#14905E', greenLt: '#E6F6EF',
  orange: '#B4610B', orangeLt: '#FFF1E0', purple: '#6B3FD1', purpleLt: '#F3EEFF',
} as const;
export const FONT = "'Pretendard Variable','Pretendard','Apple SD Gothic Neo',system-ui,-apple-system,'Malgun Gothic',sans-serif";

export function useWide(min = 1024): boolean {
  const [w, setW] = React.useState<number>(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
  React.useEffect(() => { const h = () => setW(window.innerWidth); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return w >= min;
}

/** 한글 입력 — 조합 중에는 바깥으로 안 보냄(마지막 글자 끊김 방지) */
export function KInput({ value, onChange, ...rest }: { value: string; onChange: (v: string) => void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [local, setLocal] = React.useState(value);
  const comp = React.useRef(false);
  React.useEffect(() => { if (!comp.current) setLocal(value); }, [value]);
  return <input value={local} onCompositionStart={() => { comp.current = true; }}
    onCompositionEnd={(e) => { comp.current = false; onChange(e.currentTarget.value); }}
    onChange={(e) => { setLocal(e.target.value); if (!comp.current) onChange(e.target.value); }}
    {...rest} style={{ ...inputSt, ...(rest.style || {}) }} />;
}
export const inputSt: React.CSSProperties = { width: '100%', height: 54, padding: '0 16px', borderRadius: 12, border: '1.5px solid transparent', fontSize: 17, color: X.text, background: X.field, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', transition: 'border-color .15s, background .15s' };

export function Label({ children, hint, htmlFor }: { children: React.ReactNode; hint?: string; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 14, fontWeight: 600, color: X.label, marginBottom: 8 }}>
      {children}{hint && <span style={{ fontSize: 13, fontWeight: 400, color: X.muted }}>{hint}</span>}
    </label>
  );
}

/** 칩 — 업종 · 규모 · 직함 (높이 42 · 시안) */
export function Chip({ on, onClick, children, title }: { on: boolean; onClick: () => void; children: React.ReactNode; title?: string }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick} title={title} style={{
      minHeight: 42, padding: '0 16px', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
      border: `1.5px solid ${on ? X.blue : X.line}`, background: on ? X.blueLt : X.card, color: on ? X.blue : X.label,
    }}>{children}</button>
  );
}

export function Btn({ kind = 'primary', children, onClick, disabled, style, busy }: { kind?: 'primary' | 'secondary' | 'ghost'; children: React.ReactNode; onClick?: () => void; disabled?: boolean; style?: React.CSSProperties; busy?: boolean }) {
  const S: Record<string, React.CSSProperties> = {
    primary: { background: X.blue, color: '#fff', border: 'none' },
    secondary: { background: X.soft, color: X.text, border: 'none' },
    ghost: { background: 'transparent', color: X.sub, border: 'none' },
  };
  return (
    <button type="button" onClick={disabled || busy ? undefined : onClick} disabled={disabled} aria-busy={busy || undefined} style={{
      height: 54, padding: '0 22px', borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: disabled ? 'default' : busy ? 'progress' : 'pointer', fontFamily: 'inherit',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, whiteSpace: 'nowrap',
      ...(disabled ? { background: X.blueOff, color: '#fff', border: 'none' } : S[kind]), ...style,
    }}>
      {busy && <span aria-hidden style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid currentColor', borderTopColor: 'transparent', animation: 'taxspin .8s linear infinite' }} />}
      {children}
    </button>
  );
}

export function Tag({ tone, children }: { tone: 'orange' | 'purple' | 'gray' | 'blue' | 'green'; children: React.ReactNode }) {
  const T: Record<string, [string, string]> = { orange: [X.orangeLt, X.orange], purple: [X.purpleLt, X.purple], gray: ['#F3F4F6', X.label], blue: ['#EAF2FF', X.blue], green: [X.greenLt, X.green] };
  const [bg, fg] = T[tone];
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: bg, color: fg, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', lineHeight: 1.5 }}>{children}</span>;
}

/** 아이콘 그림(흰 선 2px · 24판) */
export const GLYPH: Record<string, React.ReactNode> = {
  letter: <><rect x="5" y="3" width="14" height="18" rx="2.5" /><path d="M9 9h6M9 13h6M9 17h3" /></>,
  loan: <><path d="M3 9l9-5 9 5" /><path d="M6 11v6M10 11v6M14 11v6M18 11v6M3 20h18" /></>,
  report: <><rect x="5" y="3" width="14" height="18" rx="2.5" /><path d="M9 17v-4M12 17V9M15 17v-6" /></>,
  back: <path d="M15 5l-7 7 7 7" />,
  check: <path d="M5 12.5l5 5 9-10" />,
};
export function Glyph({ name, size = 32, color = '#fff', stroke = 2 }: { name: keyof typeof GLYPH; size?: number; color?: string; stroke?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden>{GLYPH[name]}</svg>;
}

export const card: React.CSSProperties = { background: X.card, borderRadius: 20 };

/** 동그란 체크 표시(시안의 고르기 점) — on 이면 채운 원 + 체크, 아니면 속 빈 원(2026-10-13 검수: 회색 원 + 흰 체크는 이미 고른 것처럼 보였음 · 고령자) */
export function Dot({ on, size = 22, tone }: { on: boolean; size?: number; tone?: string }) {
  return (
    <span aria-hidden style={{ flex: 'none', width: size, height: size, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', background: on ? (tone || X.blue) : X.card, border: on ? 'none' : '2px solid #8B9099', transition: 'background .15s' }}>
      {on && <Glyph name="check" size={Math.round(size * 0.55)} color="#fff" stroke={3.5} />}
    </span>
  );
}

/** 기능 화면 틀 — 「‹ 처음으로」 + 큰 제목 + 한 줄 설명 (시안) · max = 신청서 640 / 소식지 1200 */
export function Shell({ children, onHome, title, sub, wide, max = 1200 }: { children: React.ReactNode; onHome: () => void; title: React.ReactNode; sub?: string; wide: boolean; max?: number }) {
  return (
    <div style={{ maxWidth: max, margin: '0 auto', padding: wide ? '24px 20px 40px' : '16px 16px 0', boxSizing: 'border-box' }}>
      <button type="button" onClick={onHome} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 8px 6px 2px', border: 'none', background: 'transparent', color: X.sub, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
        <Glyph name="back" size={18} color="currentColor" stroke={2.2} /> 처음으로
      </button>
      <div style={{ padding: '4px 4px 20px' }}>
        <h1 style={{ fontSize: wide ? 28 : 24, fontWeight: 700, color: X.text, margin: '0 0 8px', lineHeight: 1.35, letterSpacing: '-0.02em' }}>{title}</h1>
        {sub && <p style={{ margin: 0, fontSize: 15, color: X.sub }}>{sub}</p>}
      </div>
      {children}
    </div>
  );
}

/** 단계 카드 — 「n / of」 + 제목 + 한 줄 설명 (시안) */
export function Section({ n, of, title, hint, sideHint, right, children, tone, big }: { n: number; of?: number; title: string; hint?: string; sideHint?: string; right?: React.ReactNode; children: React.ReactNode; tone?: string; big?: boolean }) {
  return (
    <section style={{ ...card, padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
          {n > 0 && <span style={{ fontSize: big ? 15 : 13, fontWeight: big ? 700 : 600, color: tone || X.blue }}>{of ? `${n} / ${of}` : n}</span>}   {/* n = 0 이면 번호 없음(한 번 내려 보는 화면 · 2026-10-14 검수) */}
          <h2 style={{ fontSize: big ? 21 : 19, fontWeight: 700, color: X.text, margin: 0 }}>{title}{sideHint && <span style={{ fontSize: 14, fontWeight: 400, color: X.muted, marginLeft: 6 }}>{sideHint}</span>}</h2>
          {hint && <p style={{ margin: 0, fontSize: big ? 16 : 14, color: big ? X.label : X.sub, lineHeight: 1.5 }}>{hint}</p>}
        </div>
        {right && <span style={{ flex: 'none' }}>{right}</span>}
      </div>
      {children}
    </section>
  );
}
